import React, { useEffect, useState } from "react";
import { Badge, ButtonGroup, Dropdown, DropdownButton, Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, LocaleDropdown } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

const List = ({ header, gameId }) => {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppApi(url_reverse("api:game:achievement:list", { game_id: gameId }));
    const { setDelData } = useAppContext();
    const [masterChecked, setMasterChecked] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const { api_post } = useRequest();

    useEffect(() => {
        if (masterChecked === true) {
            const tmpArray = [];
            apiData.object_list?.forEach((item) => {
                tmpArray.push(item.id);
            });
            setSelectedItems(tmpArray);
        } else {
            setSelectedItems([]);
        }
    }, [masterChecked]);

    if (!apiData) return;

    const selectToggle = (id) => {
        const copy = [...selectedItems];
        const index = copy.indexOf(id);

        if (index === -1) {
            copy.push(id);
        } else {
            copy.splice(index, 1);
        }
        setSelectedItems(copy);
    };

    const bulkAction = (action) => {
        api_post(null, apiUrl, {
            params: { action, ids: selectedItems },
            then: (resp) => {
                setApiData(resp);
                setSelectedItems([]);
                setMasterChecked(false);
            },
        });
    };

    const exportHandle = (action) => {
        api_post(null, apiUrl, {
            params: { action, responseType: "arrayBuffer" },
            then: (resp) => {
                const fileName = "export.csv";
                const file = new Blob([resp], { type: "text/csv" });
                const link = document.createElement("a");
                link.href = URL.createObjectURL(file);
                link.download = fileName;
                link.click();
                URL.revokeObjectURL(link.href);
            },
        });
    };

    const headerTools = (
        <>
            <DropdownButton as={ButtonGroup} size="sm" variant="dark" title={t("С выбранными")}>
                <Dropdown.Item onClick={() => bulkAction("publish")}>{t("Опубликовать")}</Dropdown.Item>
                <Dropdown.Item onClick={() => bulkAction("unpublish")}>{t("Снять с публикации")}</Dropdown.Item>
            </DropdownButton>
            <DropdownButton as={ButtonGroup} size="sm" variant="dark" title={t("Экспорт")} className="ml-2">
                <Dropdown.Item onClick={() => exportHandle("export_all")}>{t("Все")}</Dropdown.Item>
                <Dropdown.Item onClick={() => exportHandle("export_ru")}>ru_RU</Dropdown.Item>
                <Dropdown.Item onClick={() => exportHandle("export_en")}>en_US</Dropdown.Item>
            </DropdownButton>
            <Link
                className="btn btn-sm btn-dark ml-2"
                to={url_reverse("games:achievement:addmulti", { game_id: gameId })}
            >
                {t("Импорт из файла")}
            </Link>
            <Link to={url_reverse("games:achievement:add", { game_id: gameId })} className="btn btn-sm btn-dark ml-2">
                <Awesome type="plus-circle" /> {t("Добавить")}
            </Link>
        </>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th style={{ width: "3%" }}>
                            <DevForm.Checkbox
                                checked={masterChecked}
                                onChange={() => setMasterChecked(!masterChecked)}
                            />
                        </th>
                        <th style={{ width: "5%" }}>ID</th>
                        <th style={{ width: "65%" }}>{t("Название")}</th>
                        <th style={{ width: "7%" }}>{t("Локаль")}</th>
                        <th style={{ width: "10%" }}>{t("Опубликовано")}</th>
                        <th style={{ width: "10%" }}>{t("Приоритет")}</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((achievement) => (
                        <tr key={achievement.id}>
                            <td>
                                <DevForm.Checkbox
                                    checked={selectedItems.includes(achievement.id)}
                                    onChange={() => selectToggle(achievement.id)}
                                />
                            </td>
                            <td>{achievement.id}</td>
                            <td>
                                <Link
                                    to={url_reverse("games:achievement:edit", {
                                        game_id: gameId,
                                        locale: achievement.__model_locale_object.locale,
                                        achievement_id: achievement.id,
                                    })}
                                >
                                    {achievement.__model_locale_object.name}
                                </Link>
                            </td>
                            <td>{achievement.__model_locale_object.locale}</td>
                            <td>
                                {achievement.is_published === true ? (
                                    <Badge variant="success">{t("Да")}</Badge>
                                ) : (
                                    <Badge variant="secondary">{t("Нет")}</Badge>
                                )}
                            </td>
                            <td>{achievement.priority}</td>
                            <td className="dmg-table-row-tools">
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() =>
                                        setDelData({
                                            url: url_reverse("api:game:achievement:delete", {
                                                game_id: gameId,
                                                achievement_id: achievement.id,
                                            }),
                                        })
                                    }
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
};

const Edit = ({ id, locl, header, gameId }) => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:achievement:edit", { game_id: gameId, achievement_id: id }));
    const onFormSubmit = useSubmit();
    const [curLoc, setCurLoc] = useState(locl);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (!apiData) return;

    const headerTools = apiData.active_locales && (
        <LocaleDropdown
            curLoc={curLoc}
            setCurLoc={setCurLoc}
            active_locales={apiData.active_locales}
            base_locale={gameCommonInfo.game.base_locale}
            forms_data={apiData.forms_data}
        />
    );

    return (
        <CommonCard header={header} headerTools={headerTools}>
            <Form>
                {curLoc && apiData.forms_data && <RenderForm prefix={curLoc} bareForm={true} />}
                <RenderForm bareForm={true} />
                <div className="d-flex justify-content-between">
                    <DevForm.Button
                        variant="primary"
                        type="submit"
                        name="action"
                        value="save"
                        onClick={(ev) =>
                            onFormSubmit(ev, { backUrl: url_reverse("games:achievement:list", { game_id: gameId }) })
                        }
                    >
                        {t("Сохранить")}
                    </DevForm.Button>
                    <DevForm.Button
                        variant="primary"
                        type="submit"
                        name="action"
                        value="save_and_add"
                        onClick={(ev) =>
                            onFormSubmit(ev, { backUrl: url_reverse("games:achievement:add", { game_id: gameId }) })
                        }
                    >
                        {t("Сохранить и добавить еще")}
                    </DevForm.Button>
                </div>
            </Form>
        </CommonCard>
    );
};

const MultiAdd = ({ header, gameId }) => {
    const { apiData } = useAppApi(url_reverse("api:game:achievement:addmulti", { game_id: gameId }));
    const { t } = useTranslation();

    if (!apiData) return;

    const headerTools = (
        <>
            <ExportButton size="sm" buildReport="games" buttonText={t("Скачать шаблон")} />
        </>
    );

    return (
        <CommonCard header={header} headerTools={headerTools}>
            <RenderForm backUrl={url_reverse("games:achievement:list", { game_id: gameId })} />
        </CommonCard>
    );
};

export default function Achievements(props) {
    const params = useParams();

    if (params.id === "addmulti") {
        return <MultiAdd {...props} />;
    }

    if (params.id) {
        props = { ...props, id: params.id };
        return <Edit {...props} />;
    }
    return <List {...props} />;
}
